import type { RouteLocationNormalized } from 'vue-router';
import posthog from 'posthog-js';
import { serviceProviderInterface } from '@/api/serviceProviders';
import router from '@/router';
import { useNarrative } from './useNarrative';

export interface AnalyticsKeys {
    posthog: {
        apiKey: string
        apiHost: string
    }
}

export interface Analytics {
    initAnalytics: (keys: AnalyticsKeys) => void
    recordPageVisit: () => void
    recordEvent: (name: string, properties?: object) => void
    resetAnalytics: () => void
}

export default function useAnalytics(): { initAnalytics: () => void; recordPageVisit: () => void; recordEvent: (name: string, properties?: object) => void; resetAnalytics: () => void; } {
    const { currentUserOptional, userHasRoleOnCurrentServiceProvider, userIsOwnerOnServiceProvider, userHasRoleOnCurrentMerchant, currentServiceProviderOptional, currentMerchantOptional, merchants } = useNarrative();

    const groups = {
        serviceProvider: currentServiceProviderOptional.value?.id.toString() ?? '',
        merchant: currentMerchantOptional.value?.id.toString() ?? '',
    };

    // Routes can opt out of pageview tracking with `meta: { trackPageview: false }`.
    // Consecutive captures of the same path are suppressed so query/hash-only
    // navigations don't bill a redundant $pageview.
    let lastTrackedPath: string | null = null;

    const capturePageview = (route: Pick<RouteLocationNormalized, 'path' | 'meta'>): void => {
        if (!posthog.__loaded) return;
        if (route.meta.trackPageview === false) return;
        if (route.path === lastTrackedPath) return;

        lastTrackedPath = route.path;
        posthog.capture('$pageview', {
            $groups: groups,
        });
    };

    const recordPageVisit = (): void => {
        capturePageview(router.currentRoute.value);
    };

    // Signatures of the last identity/group payloads sent to PostHog, persisted
    // alongside PostHog's own browser-stored identity so they survive page loads.
    const identitySignatureKey = 'analytics:identify';
    const merchantSignatureKey = 'analytics:group:merchant';
    const serviceProviderSignatureKey = 'analytics:group:serviceProvider';
    const signatureKeys = [identitySignatureKey, merchantSignatureKey, serviceProviderSignatureKey];

    const sendWhenChanged = (storageKey: string, signature: string, send: () => void): void => {
        if (localStorage.getItem(storageKey) === signature) return;

        send();
        localStorage.setItem(storageKey, signature);
    };

    const serviceProviderDomain = (serviceProvider: serviceProviderInterface | undefined): string => {
        if (!serviceProvider) return '';

        return serviceProvider.domainName
            ? `https://${serviceProvider.domainName}`
            : origin.replace('admin', serviceProvider.subdomainName).replace('demo', serviceProvider.subdomainName);
    };

    const initAnalytics = (): void => {
        if (!posthog.__loaded) return;

        if (currentUserOptional.value) {
            const userProperties = {
                id: currentUserOptional.value.id,
                name: currentUserOptional.value.name,
                email: currentUserOptional.value.email,
                createdAt: currentUserOptional.value.createdAt,
                serviceProviderName: currentServiceProviderOptional.value?.name ?? '',
                serviceProviderUser: userHasRoleOnCurrentServiceProvider.value,
                merchantUser: userHasRoleOnCurrentMerchant.value,
                serviceProviderOwner: userIsOwnerOnServiceProvider.value,
                serviceProviderNumberOfClients: merchants.value.length,
                serviceProviderBaseUrl: serviceProviderDomain(currentServiceProviderOptional.value),
            };

            // PostHog persists identity and group membership in the browser and
            // auto-attaches them to every capture, so re-sending unchanged payloads on
            // each mount only bills redundant $set / $groupidentify events. Fire only
            // when the payload changes, so genuine property updates (role granted,
            // client added, merchant renamed) still reach PostHog without billing no-ops.
            const distinctId = userProperties.id.toString();
            sendWhenChanged(identitySignatureKey, JSON.stringify(userProperties), () => {
                posthog.identify(distinctId, userProperties);
            });

            const merchant = currentMerchantOptional.value;
            if (merchant) {
                const merchantProperties = { name: merchant.name };
                sendWhenChanged(merchantSignatureKey, JSON.stringify({ id: merchant.id, ...merchantProperties }), () => {
                    posthog.group('merchant', merchant.id.toString(), merchantProperties);
                });
            }

            const serviceProvider = currentServiceProviderOptional.value;
            if (serviceProvider) {
                const serviceProviderProperties = { name: serviceProvider.name };
                sendWhenChanged(serviceProviderSignatureKey, JSON.stringify({ id: serviceProvider.id, ...serviceProviderProperties }), () => {
                    posthog.group('serviceProvider', serviceProvider.id.toString(), serviceProviderProperties);
                });
            }
        }
        // Capture the landing page explicitly: this hook is registered after the
        // initial navigation has already resolved, and `capture_pageview: false`
        // means PostHog no longer captures it automatically.
        recordPageVisit();

        router.afterEach((to) => {
            capturePageview(to);
        });
    };

    const recordEvent = (name: string, properties: object = {}): void => {
        if (!posthog.__loaded) return;

        const propertiesWithGroup = Object.assign(properties, {
            $groups: groups,
        });
        posthog.capture(name, propertiesWithGroup);
    };

    const resetAnalytics = (): void => {
        if (!posthog.__loaded) return;

        // Clear the stored signatures so the next login always re-identifies, since
        // posthog.reset() drops the persisted identity and group membership.
        signatureKeys.forEach(key => localStorage.removeItem(key));
        posthog.reset();
    };

    return {
        initAnalytics,
        recordPageVisit,
        recordEvent,
        resetAnalytics,
    };
}
